/**
 * The promotion filters shared by the Calendar and Accrual views. Both pages filter the same
 * promotions with the same chips, so they must agree on what a selection means — the Accrual page
 * once had its own single-select, exact-match dropdowns and dropped rows the Calendar showed.
 */

/** The fields a promotion needs to be filtered. Both `Promotion` and `AccrualRow` satisfy it. */
export interface FilterablePromotion {
  name: string
  description?: string | null
  remote_promo_id?: string | null
  customer?: string | null
  retailer?: string | null
  promotion_type?: string | null
  products?: string[] | null
}

export interface PromotionFilterSelection {
  customers: string[]
  retailers: string[]
  promotion_types: string[]
  products: string[]
  search: string
}

export const EMPTY_PROMOTION_FILTERS: PromotionFilterSelection = {
  customers: [],
  retailers: [],
  promotion_types: [],
  products: [],
  search: "",
}

export interface PromotionFilterOptions {
  customers: string[]
  retailers: string[]
  promotionTypes: string[]
  products: string[]
}

/** Distinct, sorted chip values for every filter category. */
export function promotion_filter_options(promotions: FilterablePromotion[]): PromotionFilterOptions {
  const customers = new Set<string>()
  const retailers = new Set<string>()
  const promotion_types = new Set<string>()
  const products = new Set<string>()

  for (const promotion of promotions) {
    if (promotion.customer) customers.add(promotion.customer)
    if (promotion.retailer) retailers.add(promotion.retailer)
    if (promotion.promotion_type) promotion_types.add(promotion.promotion_type)
    for (const product of promotion.products ?? []) products.add(product)
  }

  return {
    customers: Array.from(customers).sort(),
    retailers: Array.from(retailers).sort(),
    promotionTypes: Array.from(promotion_types).sort(),
    products: Array.from(products).sort(),
  }
}

/** Whether any chip is selected (the free-text search does not count). */
export function has_active_chip_filters(selection: PromotionFilterSelection): boolean {
  return (
    selection.customers.length > 0 ||
    selection.retailers.length > 0 ||
    selection.promotion_types.length > 0 ||
    selection.products.length > 0
  )
}

/**
 * Whether a promotion passes every selected filter. Chips within a category are OR'd, categories
 * are AND'd. A promotion with no value for a category is not excluded by that category's chips,
 * matching how the Calendar has always behaved.
 *
 * The search matches the promotion's own words — name, description, promo number, products — and
 * also its type, retailer and distributor, so typing "TPR" finds "SCANS/BILL BACKS/TPR" promotions
 * whatever they are named.
 */
export function promotion_matches_filters(promotion: FilterablePromotion, selection: PromotionFilterSelection): boolean {
  if (selection.customers.length > 0 && promotion.customer && !selection.customers.includes(promotion.customer)) {
    return false
  }
  if (selection.retailers.length > 0 && promotion.retailer && !selection.retailers.includes(promotion.retailer)) {
    return false
  }
  if (
    selection.promotion_types.length > 0 &&
    promotion.promotion_type &&
    !selection.promotion_types.includes(promotion.promotion_type)
  ) {
    return false
  }
  if (selection.products.length > 0 && promotion.products) {
    if (!promotion.products.some((product: string) => selection.products.includes(product))) return false
  }

  const search = selection.search.trim().toLowerCase()
  if (search) {
    const haystack = [
      promotion.name,
      promotion.description,
      promotion.remote_promo_id,
      promotion.promotion_type,
      promotion.retailer,
      promotion.customer,
      ...(promotion.products ?? []),
    ]
    if (!haystack.some((value: string | null | undefined) => value?.toLowerCase().includes(search))) return false
  }

  return true
}
